import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import AppHeader from "./layout/app-header";
import { NavigationItemData } from "./layout/mobile-menu";
import { useAuth } from "../contexts/AuthContext";
import { INVENTORY_NAV_ITEMS } from "../config/task-views";
import { stagingLinks } from "../config/externalLinks";

interface LayoutProps {
  children: React.ReactNode;
}

/**
 * Main application layout wrapper for Inventory Planning Frontend.
 * Manages theme state, page titles, and renders the header with navigation.
 */
const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("theme");
    return (
      saved === "dark" ||
      (saved == null && window.matchMedia("(prefers-color-scheme: dark)").matches)
    );
  });

  // Toggle theme and persist to localStorage
  const toggleTheme = (): void => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem("theme", newTheme ? "dark" : "light");
  };

  // Apply theme to document element
  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      root.classList.add("dark");
      root.classList.remove("light");
    } else {
      root.classList.add("light");
      root.classList.remove("dark");
    }
  }, [isDarkMode]);

  // Update page title based on current route
  useEffect(() => {
    const pathname = location.pathname;
    let title = "Inventory Planning";

    if (pathname === "/ui-kit") {
      title = "UI Kit - Inventory Planning";
    } else if (pathname === "/") {
      title = "Inventory Planning";
    } else if (pathname.startsWith("/inventory")) {
      const view = INVENTORY_NAV_ITEMS.find((v) => v.path === pathname);
      title = view != null ? `${view.name} - Inventory Planning` : "Inventory Planning";
    } else if (pathname === "/profile") {
      title = "Profile - Inventory Planning";
    }

    document.title = title;
  }, [location.pathname]);

  const navigation: NavigationItemData[] = [
    ...INVENTORY_NAV_ITEMS.map((view) => ({
      name: view.name,
      href: view.path,
      current: location.pathname === view.path,
    })),
    // {
    //   name: "UI Kit",
    //   href: "/ui-kit",
    //   current: location.pathname === "/ui-kit",
    // },
    {
      name: "Inventory Staging",
      href: stagingLinks.base(),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <AppHeader
        navigation={navigation}
        user={user}
        isDarkMode={isDarkMode}
        onThemeToggle={toggleTheme}
        onLogout={() => void logout()}
      />

      {/* Main content */}
      <main className="flex-1 relative z-0">
        <div className="w-full py-6 sm:px-6 lg:px-8">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
